import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  growthLeads, growthLeadUpdate, growthLeadNote, growthLeadTask, growthTaskUpdate,
  growthNotifications, growthNotificationsRead,
  growthStories, growthStorySave, growthStoryDelete,
  growthTestimonials, growthTestimonialSave, growthTestimonialDelete,
} from "@/lib/api";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Funnel, Bell, Plus, Trash, PencilSimple, CheckCircle, Circle, Globe, Phone, EnvelopeSimple, ClockCounterClockwise, Quotes, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { GrowthMetrics } from "@/components/GrowthMetrics";

const COLUMNS = [
  ["new", "New Lead", "bg-amber-500/15 text-amber-300 border-amber-500/25"],
  ["contacted", "Contacted", "bg-sky-500/15 text-sky-300 border-sky-500/25"],
  ["demo_scheduled", "Demo Scheduled", "bg-violet-500/15 text-violet-300 border-violet-500/25"],
  ["demo_completed", "Demo Completed", "bg-indigo-500/15 text-indigo-300 border-indigo-500/25"],
  ["trial_started", "Trial Started", "bg-emerald-500/15 text-emerald-300 border-emerald-500/25"],
  ["customer", "Customer", "bg-emerald-600/20 text-emerald-400 border-emerald-500/30"],
  ["lost", "Lost", "bg-red-500/12 text-red-300/80 border-red-500/20"],
];
const COL_LABEL = Object.fromEntries(COLUMNS.map(([k, l]) => [k, l]));

function LeadDetail({ lead, onClose, refetch }) {
  const [note, setNote] = useState("");
  const [task, setTask] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [taskReminder, setTaskReminder] = useState(false);
  const patch = useMutation({
    mutationFn: (body) => growthLeadUpdate(lead.id, body),
    onSuccess: () => { refetch(); toast.success("Lead updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });
  const addNote = useMutation({
    mutationFn: () => growthLeadNote(lead.id, note),
    onSuccess: () => { setNote(""); refetch(); toast.success("Note added"); },
  });
  const addTask = useMutation({
    mutationFn: () => growthLeadTask(lead.id, { title: task, due_at: taskDue, reminder: taskReminder }),
    onSuccess: () => { setTask(""); setTaskDue(""); setTaskReminder(false); refetch(); toast.success("Task added"); },
  });
  const toggleTask = useMutation({
    mutationFn: ({ tid, done }) => growthTaskUpdate(lead.id, tid, { done }),
    onSuccess: refetch,
  });
  const Row = ({ icon: Icon, label, value, href }) => value ? (
    <div className="flex items-center gap-2 text-xs text-[#cfcfcf]">
      <Icon size={13} className="text-[#7a7a7a] shrink-0" />
      <span className="text-[#7a7a7a]">{label}:</span>
      {href ? <a href={href} target="_blank" rel="noreferrer" className="brand-text hover:underline truncate">{value}</a> : <span className="truncate">{value}</span>}
    </div>
  ) : null;

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-[#0d0d13] border-white/10 text-white w-full sm:max-w-lg overflow-y-auto" data-testid="lead-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-head text-white">{lead.business}</SheetTitle>
        </SheetHeader>
        <div className="space-y-5 mt-4">
          <div className="rounded-xl border border-white/8 bg-white/[0.03] p-4 space-y-2">
            <div className="text-sm font-semibold">{lead.name}</div>
            <Row icon={EnvelopeSimple} label="Email" value={lead.email} href={`mailto:${lead.email}`} />
            <Row icon={Phone} label="Phone" value={lead.phone} href={lead.phone ? `tel:${lead.phone}` : null} />
            <Row icon={Globe} label="Website" value={lead.website} href={lead.website?.startsWith("http") ? lead.website : lead.website ? `https://${lead.website}` : null} />
            <Row icon={Funnel} label="Source" value={lead.source} />
            <div className="grid grid-cols-2 gap-2 pt-1 text-xs">
              {lead.company_size && <div><span className="text-[#7a7a7a]">Size:</span> {lead.company_size}</div>}
              {lead.industry && <div><span className="text-[#7a7a7a]">Industry:</span> {lead.industry}</div>}
            </div>
            {lead.current_tools && <div className="text-xs"><span className="text-[#7a7a7a]">Current tools:</span> {lead.current_tools}</div>}
            {lead.biggest_challenge && (
              <div className="text-xs rounded-lg bg-[#6B3DFF]/[0.07] border border-[#6B3DFF]/20 p-2.5 mt-1">
                <span className="label-caps text-[9px] brand-text block mb-1">Biggest challenge</span>{lead.biggest_challenge}
              </div>
            )}
          </div>

          <div>
            <div className="label-caps mb-1.5">Pipeline stage</div>
            <select data-testid="lead-status-select" value={lead.status} onChange={(e) => patch.mutate({ status: e.target.value })}
              className="w-full h-10 rounded-lg bg-[#10101A] border border-white/10 px-3 text-sm">
              {COLUMNS.map(([k, l]) => <option key={k} value={k} className="bg-[#161622]">{l}</option>)}
            </select>
            <Input data-testid="lead-followup-input" defaultValue={lead.followup_status || ""} placeholder="Follow-up status (e.g. call booked Fri 2pm)"
              onBlur={(e) => e.target.value !== (lead.followup_status || "") && patch.mutate({ followup_status: e.target.value })}
              className="bg-[#10101A] border-white/10 h-9 mt-2 text-sm" />
          </div>

          <div>
            <div className="label-caps mb-1.5">Tasks & follow-up reminders</div>
            <div className="space-y-1.5">
              {(lead.tasks || []).map((t) => (
                <button key={t.id} data-testid={`lead-task-${t.id}`} onClick={() => toggleTask.mutate({ tid: t.id, done: !t.done })}
                  className="w-full flex items-center gap-2 rounded-lg border border-white/8 bg-white/[0.03] px-3 py-2 text-left text-xs hover:border-white/20 transition-colors">
                  {t.done ? <CheckCircle size={15} className="text-emerald-400 shrink-0" weight="fill" /> : <Circle size={15} className="text-[#7a7a7a] shrink-0" />}
                  <span className={`flex-1 ${t.done ? "line-through text-[#5a5a5a]" : "text-[#cfcfcf]"}`}>{t.title}</span>
                  {t.due_at && <span className="text-[10px] text-amber-300/80 shrink-0">{t.due_at}</span>}
                  {t.reminder && <Bell size={11} className="brand-text shrink-0" weight="fill" />}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input data-testid="lead-task-input" value={task} onChange={(e) => setTask(e.target.value)} placeholder="Add task…" className="bg-[#10101A] border-white/10 h-9 text-sm flex-1" />
              <input type="date" data-testid="lead-task-due" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} className="h-9 rounded-lg bg-[#10101A] border border-white/10 px-2 text-xs" />
              <Button data-testid="lead-task-add" size="sm" onClick={() => task.trim() && addTask.mutate()} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-9"><Plus size={14} /></Button>
            </div>
            <label className="mt-1.5 flex items-center gap-2 text-[11px] text-[#9a9a9a] cursor-pointer">
              <input type="checkbox" data-testid="lead-task-reminder" checked={taskReminder} onChange={(e) => setTaskReminder(e.target.checked)} className="accent-[#6B3DFF]" />
              Mark as follow-up reminder
            </label>
          </div>

          <div>
            <div className="label-caps mb-1.5">Notes</div>
            <div className="space-y-1.5 max-h-44 overflow-y-auto">
              {(lead.notes || []).slice().reverse().map((n) => (
                <div key={n.id} className="rounded-lg border border-white/8 bg-white/[0.03] px-3 py-2 text-xs text-[#cfcfcf]" data-testid={`lead-note-${n.id}`}>
                  {n.text}
                  <div className="text-[9px] text-[#5a5a5a] mt-1">{n.by} · {(n.at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
              ))}
              {!(lead.notes || []).length && <div className="text-xs text-[#5a5a5a]">No notes yet.</div>}
            </div>
            <div className="flex gap-2 mt-2">
              <Textarea data-testid="lead-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note…" className="bg-[#10101A] border-white/10 text-sm flex-1" />
              <Button data-testid="lead-note-add" size="sm" onClick={() => note.trim() && addNote.mutate()} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-9 self-end"><Plus size={14} /></Button>
            </div>
          </div>

          <div>
            <div className="label-caps mb-1.5 flex items-center gap-1.5"><ClockCounterClockwise size={12} /> History</div>
            <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="lead-history">
              {(lead.history || []).slice().reverse().map((h, i) => (
                <div key={i} className="text-[11px] text-[#9a9a9a] flex gap-2">
                  <span className="text-[#5a5a5a] shrink-0">{(h.at || "").slice(5, 16).replace("T", " ")}</span>
                  <span>{h.event}</span>
                </div>
              ))}
            </div>
          </div>

          <button data-testid="lead-archive-btn" onClick={() => { patch.mutate({ archived: true }); onClose(); }}
            className="text-[11px] text-[#7a7a7a] hover:text-red-400 underline underline-offset-2">Archive this lead</button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ContentEditor({ kind, listFn, saveFn, deleteFn, fields, icon: Icon }) {
  const qc = useQueryClient();
  const key = ["growth", kind];
  const { data: rows = [] } = useQuery({ queryKey: key, queryFn: listFn });
  const [edit, setEdit] = useState(null);
  const save = useMutation({
    mutationFn: (body) => saveFn(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); setEdit(null); toast.success("Saved — live on the marketing site"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const del = useMutation({
    mutationFn: (id) => deleteFn(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Deleted"); },
  });
  return (
    <div data-testid={`${kind}-editor`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-[#9a9a9a]">{rows.length} item(s) · published ones appear on the public site</div>
        <Button data-testid={`${kind}-add-btn`} size="sm" onClick={() => setEdit({ published: true, placeholder: false })} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-9 rounded-full px-4">
          <Plus size={14} className="mr-1" /> Add new
        </Button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {rows.map((r) => (
          <div key={r.id} className="card-matte p-4" data-testid={`${kind}-item-${r.id}`}>
            <div className="flex items-center gap-2">
              <Icon size={15} className="brand-text shrink-0" weight="duotone" />
              <span className="text-sm font-semibold flex-1 truncate">{r.business || r.name}</span>
              {r.placeholder && <span className="text-[9px] font-bold rounded-full bg-amber-500/15 text-amber-300 px-2 py-0.5">SAMPLE</span>}
              <span className={`text-[9px] font-bold rounded-full px-2 py-0.5 ${r.published ? "bg-emerald-500/15 text-emerald-400" : "bg-white/10 text-[#7a7a7a]"}`}>{r.published ? "LIVE" : "DRAFT"}</span>
            </div>
            <p className="mt-2 text-xs text-[#9a9a9a] line-clamp-2">{r.quote || r.problem}</p>
            <div className="mt-3 flex gap-2">
              <Button data-testid={`${kind}-edit-${r.id}`} size="sm" variant="ghost" onClick={() => setEdit(r)} className="text-[#9a9a9a] hover:text-white h-8 px-2 border border-white/10"><PencilSimple size={13} className="mr-1" /> Edit</Button>
              <Button data-testid={`${kind}-toggle-${r.id}`} size="sm" variant="ghost" onClick={() => save.mutate({ ...r, published: !r.published })} className="text-[#9a9a9a] hover:text-white h-8 px-2 border border-white/10">{r.published ? "Unpublish" : "Publish"}</Button>
              <Button data-testid={`${kind}-delete-${r.id}`} size="sm" variant="ghost" onClick={() => del.mutate(r.id)} className="text-[#7a7a7a] hover:text-red-400 h-8 px-2 ml-auto"><Trash size={13} /></Button>
            </div>
          </div>
        ))}
      </div>
      {edit && (
        <Dialog open onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent className="bg-[#101018] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`${kind}-edit-dialog`}>
            <DialogHeader>
              <DialogTitle className="font-head">{edit.id ? "Edit" : "New"} {kind === "stories" ? "success story" : "testimonial"}</DialogTitle>
              <DialogDescription className="text-[#7a7a7a] text-xs">Published items appear on the public marketing site immediately after saving.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {fields.map(([k, label, type]) => (
                <div key={k}>
                  <div className="label-caps mb-1">{label}</div>
                  {type === "textarea"
                    ? <Textarea data-testid={`${kind}-field-${k}`} value={edit[k] || ""} onChange={(e) => setEdit((d) => ({ ...d, [k]: e.target.value }))} rows={3} className="bg-[#10101A] border-white/10 text-sm" />
                    : <Input data-testid={`${kind}-field-${k}`} value={edit[k] || ""} onChange={(e) => setEdit((d) => ({ ...d, [k]: e.target.value }))} className="bg-[#10101A] border-white/10 h-9 text-sm" />}
                </div>
              ))}
              <label className="flex items-center gap-2 text-xs text-[#9a9a9a] cursor-pointer">
                <input type="checkbox" data-testid={`${kind}-field-published`} checked={!!edit.published} onChange={(e) => setEdit((d) => ({ ...d, published: e.target.checked }))} className="accent-[#6B3DFF]" />
                Published (visible on the public marketing site)
              </label>
              <Button data-testid={`${kind}-save-btn`} onClick={() => save.mutate({ ...edit, placeholder: false })} disabled={save.isPending}
                className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-10">{save.isPending ? "Saving…" : "Save"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default function LeadsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("pipeline");
  const [q, setQ] = useState("");
  const [detailId, setDetailId] = useState(null);
  const { data, refetch } = useQuery({ queryKey: ["growth-leads", q], queryFn: () => growthLeads(q ? { q } : {}) });
  const { data: notif } = useQuery({ queryKey: ["growth-notifs"], queryFn: growthNotifications, refetchInterval: 60000 });
  const markRead = useMutation({ mutationFn: growthNotificationsRead, onSuccess: () => qc.invalidateQueries({ queryKey: ["growth-notifs"] }) });
  const leads = data?.leads || [];
  const detail = leads.find((l) => l.id === detailId) || null;

  return (
    <PageContainer>
      <PageHeader icon={Funnel} eyebrow="Growth" title="Leads & Growth"
        subtitle="Every demo request lands here automatically. Move leads through the pipeline, keep notes and tasks, and manage the success stories & testimonials shown on your marketing site." />

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {[["pipeline", "Lead Pipeline"], ["metrics", "Growth Metrics"], ["stories", "Success Stories"], ["testimonials", "Testimonials"]].map(([k, l]) => (
          <button key={k} data-testid={`growth-tab-${k}`} onClick={() => setTab(k)}
            className={`h-9 rounded-full px-4 text-sm font-semibold border transition-colors ${tab === k ? "bg-[#6B3DFF] border-[#6B3DFF] text-white" : "bg-white/5 border-white/10 text-[#9a9a9a] hover:text-white"}`}>
            {l}
          </button>
        ))}
        {notif?.unread > 0 && (
          <button data-testid="growth-notif-btn" onClick={() => markRead.mutate()}
            className="ml-auto flex items-center gap-1.5 h-9 rounded-full px-4 text-xs font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/25">
            <Bell size={13} weight="fill" /> {notif.unread} new demo request{notif.unread > 1 ? "s" : ""} — mark read
          </button>
        )}
      </div>

      {tab === "pipeline" && (
        <>
          <Input data-testid="leads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads by name, business, email…"
            className="bg-[#10101A] border-white/10 h-10 max-w-sm mb-4" />
          <div className="flex gap-3 overflow-x-auto pb-4" data-testid="leads-kanban">
            {COLUMNS.map(([key, label, chip]) => {
              const col = leads.filter((l) => l.status === key);
              return (
                <div key={key} className="w-64 shrink-0" data-testid={`leads-column-${key}`}>
                  <div className={`rounded-full border px-3 py-1.5 text-xs font-bold inline-flex items-center gap-2 ${chip}`}>
                    {label} <span className="opacity-70">{col.length}</span>
                  </div>
                  <div className="mt-2.5 space-y-2 min-h-[80px]">
                    {col.map((l) => (
                      <button key={l.id} data-testid={`lead-card-${l.id}`} onClick={() => setDetailId(l.id)}
                        className="w-full text-left card-matte p-3.5 hover:border-[#6B3DFF]/40 border border-transparent transition-all hover:-translate-y-0.5">
                        <div className="text-sm font-semibold truncate">{l.business}</div>
                        <div className="text-xs text-[#9a9a9a] truncate">{l.name} · {l.industry || "—"}</div>
                        <div className="mt-2 flex items-center gap-2 text-[10px] text-[#5a5a5a]">
                          <span>{(l.created_at || "").slice(0, 10)}</span>
                          {(l.tasks || []).some((t) => !t.done) && <span className="text-amber-300">• {(l.tasks || []).filter((t) => !t.done).length} open task(s)</span>}
                          {(l.notes || []).length > 0 && <span>• {(l.notes || []).length} note(s)</span>}
                        </div>
                        {l.followup_status && l.followup_status !== "pending_first_contact" && (
                          <div className="mt-1 text-[10px] brand-text truncate">↳ {l.followup_status}</div>
                        )}
                      </button>
                    ))}
                    {!col.length && <div className="text-[10px] text-[#4a4a4a] text-center py-4 rounded-lg border border-dashed border-white/8">empty</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === "metrics" && <GrowthMetrics />}

      {tab === "stories" && (
        <ContentEditor kind="stories" listFn={growthStories} saveFn={growthStorySave} deleteFn={growthStoryDelete} icon={Trophy}
          fields={[["business", "Business name"], ["industry", "Industry"], ["problem", "Problem", "textarea"], ["solution", "Solution", "textarea"],
            ["time_saved", "Time saved (e.g. 6 hrs/week)"], ["revenue_increase", "Revenue / impact (e.g. +18% repeat orders)"],
            ["quote", "Customer quote", "textarea"], ["person_name", "Person name"], ["person_role", "Person role"],
            ["logo_url", "Logo URL (optional)"], ["image_url", "Screenshot / photo URL (optional)"], ["video_url", "Video URL (optional)"]]} />
      )}
      {tab === "testimonials" && (
        <ContentEditor kind="testimonials" listFn={growthTestimonials} saveFn={growthTestimonialSave} deleteFn={growthTestimonialDelete} icon={Quotes}
          fields={[["name", "Customer name"], ["company", "Company"], ["industry", "Industry"], ["role", "Role"],
            ["quote", "Quote", "textarea"], ["results", "Results line (e.g. 6+ hrs saved weekly)"],
            ["photo_url", "Photo URL (optional)"], ["video_url", "Video URL (optional)"]]} />
      )}

      {detail && <LeadDetail lead={detail} onClose={() => setDetailId(null)} refetch={refetch} />}
    </PageContainer>
  );
}
